import { Link } from "react-router-dom";
import { ArrowLeft, Check, Zap, Crown, Users, Building2, Star, Shield, Heart } from "lucide-react";

const Pricing = () => {
  const plans = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      description: "Try it out",
      icon: Zap,
      iconBg: "bg-slate-100",
      iconColor: "text-slate-600",
      forLabel: "For Students",
      features: [
        "3 questions per day",
        "Answer-only explanations",
        "All test categories",
        "Basic progress tracking"
      ],
      limitations: [
        "No full explanations",
        "No test readiness score",
        "No weakness analysis"
      ],
      cta: "Start Free",
      ctaLink: "/register",
      ctaStyle: "bg-slate-100 text-slate-700 hover:bg-slate-200",
      popular: false
    },
    {
      name: "Family Pack",
      price: "$99",
      period: "year",
      description: "Full access, progress tracking, better outcomes",
      icon: Heart,
      iconBg: "bg-white/20",
      iconColor: "text-pink-300",
      forLabel: "For Families",
      features: [
        "Unlimited practice for 4 students",
        "Test readiness scores for everyone",
        "See exactly what needs work",
        "Full step-by-step explanations",
        "AI tutor when anyone gets stuck",
        "Track progress for all students"
      ],
      cta: "Get Family Pack",
      ctaLink: "/membership?plan=family",
      ctaStyle: "bg-pink-400 text-white hover:bg-pink-300 font-bold",
      popular: true,
      gradient: true,
      gradientColors: "from-pink-500 to-rose-600"
    },
    {
      name: "Individual",
      price: "$79",
      period: "year",
      description: "Everything needed to pass",
      icon: Crown,
      iconBg: "bg-indigo-100",
      iconColor: "text-indigo-600",
      forLabel: "For Students",
      features: [
        "Unlimited practice questions",
        "Full step-by-step explanations",
        "Test readiness score",
        "Weakness & skill gap analysis",
        "Timed tests & mock exams",
        "AI tutor anytime"
      ],
      cta: "Get Individual",
      ctaLink: "/membership",
      ctaStyle: "bg-indigo-600 text-white hover:bg-indigo-700",
      popular: false
    }
  ];

  const bulkPlans = [
    {
      name: "Teacher Pack",
      price: "$99",
      period: "year",
      users: "Up to 150 students",
      description: "Help every student succeed",
      icon: Users,
      cta: "Get Started"
    },
    {
      name: "Campus Pack", 
      price: "$499",
      period: "year",
      users: "Unlimited students",
      description: "No student left behind",
      icon: Building2,
      cta: "Get Started"
    },
    {
      name: "District Pack",
      price: "Custom",
      period: "",
      users: "District-wide",
      description: "Let's talk about your needs",
      icon: Building2,
      cta: "Contact Sales"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="font-heading font-black text-4xl mb-4">
            Ready to pass? <span className="text-lime-300">Now you'll know.</span>
          </h1>
          <p className="text-white/80 text-lg">Start free. Upgrade for unlimited practice and progress tracking.</p>
        </div>
      </header>

      {/* Main Pricing Cards */}
      <main className="max-w-6xl mx-auto px-4 -mt-8">
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan, idx) => (
            <div 
              key={idx}
              className={`rounded-3xl shadow-lg p-8 relative overflow-hidden ${
                plan.gradient 
                  ? `bg-gradient-to-br ${plan.gradientColors || 'from-indigo-600 to-indigo-700'} text-white transform md:scale-105 z-10` 
                  : 'bg-white border-2 border-slate-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute top-4 right-4 px-3 py-1 bg-yellow-400 text-slate-800 text-xs font-bold rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3 fill-current" />
                  MOST POPULAR
                </div>
              )}

              {/* For Label */}
              <div className={`text-xs font-bold uppercase tracking-wide mb-2 ${plan.gradient ? 'text-white/60' : 'text-slate-400'}`}>
                {plan.forLabel}
              </div>

              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 ${plan.iconBg} rounded-xl flex items-center justify-center`}>
                  <plan.icon className={`w-6 h-6 ${plan.iconColor}`} />
                </div>
                <div>
                  <h2 className={`font-heading font-bold text-xl ${plan.gradient ? '' : 'text-slate-800'}`}>
                    {plan.name}
                  </h2>
                  <p className={`text-sm ${plan.gradient ? 'text-white/70' : 'text-slate-500'}`}>
                    {plan.description}
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <span className={`font-heading font-black text-4xl ${plan.gradient ? '' : 'text-slate-800'}`}>
                  {plan.price}
                </span>
                <span className={plan.gradient ? 'text-white/70' : 'text-slate-500'}>/{plan.period}</span>
                {plan.name === "Family Pack" && (
                  <p className="text-sm text-pink-200 mt-1">That's just $25/student/year</p>
                )}
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className={`w-5 h-5 ${plan.gradient ? 'bg-white/20' : 'bg-lime-100'} rounded-full flex items-center justify-center flex-shrink-0`}>
                      <Check className={`w-3 h-3 ${plan.gradient ? 'text-white' : 'text-lime-600'}`} />
                    </div>
                    <span className={plan.gradient ? 'text-white/90' : 'text-slate-600'}>{feature}</span>
                  </li>
                ))}
                {plan.limitations?.map((limit, i) => (
                  <li key={`limit-${i}`} className="flex items-center gap-3 opacity-50">
                    <div className="w-5 h-5 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-slate-400 text-xs">✕</span>
                    </div>
                    <span className="text-slate-500 line-through">{limit}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={plan.ctaLink}
                className={`block w-full py-4 text-center font-bold rounded-xl transition-all ${plan.ctaStyle} ${plan.gradient ? 'shadow-lg' : ''}`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>

        {/* Trust Badge */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-full shadow-sm border border-slate-200">
            <Shield className="w-5 h-5 text-green-500" />
            <span className="text-slate-600 font-medium">30-day money-back guarantee. Cancel anytime.</span>
          </div>
        </div>

        {/* Bulk Plans */}
        <div className="mt-16">
          <h2 className="font-heading font-bold text-2xl text-slate-800 text-center mb-2">
            Schools & Organizations
          </h2>
          <p className="text-slate-500 text-center mb-8">Help every student in your classroom, school, or district succeed.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {bulkPlans.map((plan, idx) => (
              <div key={idx} className="bg-white rounded-2xl border-2 border-slate-200 p-6 hover:border-indigo-300 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <plan.icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">{plan.name}</h3>
                    <p className="text-sm text-indigo-600">{plan.users}</p>
                  </div>
                </div>
                <div className="mb-4">
                  <span className="font-heading font-bold text-2xl text-slate-800">{plan.price}</span>
                  {plan.period && <span className="text-slate-500">/{plan.period}</span>}
                </div>
                <p className="text-slate-500 text-sm mb-4">{plan.description}</p>
                <Link
                  to="/contact"
                  className="block w-full py-2 text-center bg-indigo-50 text-indigo-600 font-bold rounded-xl hover:bg-indigo-100 transition-colors"
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* For Students vs For Families */}
        <div className="mt-16 grid md:grid-cols-2 gap-8">
          {/* For Students */}
          <div className="bg-indigo-50 rounded-3xl p-8 border-2 border-indigo-100">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4 flex items-center gap-2">
              <span className="text-2xl">🎓</span> For Students
            </h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">Practice questions that match the real exam</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">Daily streaks keep you motivated</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">AI tutor explains concepts when you're stuck</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">Track your progress toward Exam Ready</span>
              </li>
            </ul>
          </div>

          {/* For Families */}
          <div className="bg-pink-50 rounded-3xl p-8 border-2 border-pink-100">
            <h2 className="font-heading font-bold text-xl text-slate-800 mb-4 flex items-center gap-2">
              <span className="text-2xl">👨‍👩‍👧‍👦</span> For Families
            </h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">See test readiness scores for each student</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">Know exactly what needs more practice</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">Cover up to 4 students with one plan</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">Peace of mind before test day</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Free vs Premium Comparison */}
        <div className="mt-16 bg-white rounded-3xl border-2 border-slate-200 p-8">
          <h2 className="font-heading font-bold text-2xl text-slate-800 text-center mb-8">
            Free vs Premium
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-100">
                  <th className="text-left py-3 px-4 text-slate-600 font-medium">What you get</th>
                  <th className="text-center py-3 px-4 text-slate-600 font-medium">Free</th>
                  <th className="text-center py-3 px-4 text-pink-600 font-bold">Family Pack ⭐</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Daily practice", "3 questions", "Unlimited"],
                  ["Explanations", "Answer only", "Full step-by-step"],
                  ["AI Tutor help", "2 messages/day", "Unlimited"],
                  ["Test readiness score", "✕", "✓"],
                  ["Weakness analysis", "✕", "✓"],
                  ["Timed practice tests", "✕", "✓"],
                  ["Students covered", "1", "Up to 4"]
                ].map(([feature, free, premium], i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-3 px-4 text-slate-700">{feature}</td>
                    <td className="py-3 px-4 text-center text-slate-500">{free}</td>
                    <td className="py-3 px-4 text-center text-pink-600 font-medium">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 text-center">
            <Link
              to="/membership?plan=family"
              className="inline-block px-8 py-4 bg-gradient-to-r from-pink-500 to-rose-600 text-white font-bold rounded-xl hover:from-pink-600 hover:to-rose-700 transition-all shadow-lg"
            >
              Get Family Pack - $99/year
            </Link>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16 text-center pb-16">
          <h2 className="font-heading font-bold text-2xl text-slate-800 mb-4">Questions?</h2>
          <div className="flex items-center justify-center gap-4">
            <Link
              to="/faq"
              className="px-6 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors"
            >
              View FAQ
            </Link>
            <Link
              to="/contact"
              className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-6 text-center text-slate-500 text-sm">
        © 2025 Clear Path Testing Solutions LLC. All rights reserved.
      </footer>
    </div>
  );
};

export default Pricing;
